import React, { useCallback, useEffect, useMemo, useState ,useContext} from 'react';
import { api } from '../config/api';
import { Plus, ArrowLeft, Eye, ClipboardList, Trash2, Pencil, Search, X } from 'lucide-react';
import toast from '../utils/toast';
import { formatDisplayDate, normalizeDateInput, toDateInputValue, toDateObject } from '../utils/dateFormat';
import SearchBar from '../components/SearchBar';
import { AuthContext } from '../context/AuthContext';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterReadOnly,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTitle,
  masterTableData,
  masterToolbar,
  masterTableID
} from '../components/masterPageStyles';

const createEmptyItem = () => ({
  po_item_id: '',
  medicine_id: '',
  batch_number: '',
  mfg_date: '',
  expiry_date: '',
  ordered_quantity: '',
  received_quantity: '',
  purchase_price: '',
  pending_quantity: 0,
  mrp: ''
});

const emptyForm = {
  grn_id: '',
  grn_number: '',
  supplier_id: '',
  po_id: '',
  po_number: '',
  invoice_number: '',
  invoice_date: '',
  received_by: '',
  received_date: toDateInputValue(new Date()),
  remarks: '',
  items: [createEmptyItem()]
};

const normalizePoLookup = (value = '') => String(value).trim().toUpperCase();
const normalizeMedicineLookup = (value) => String(value || '').trim();

const normalizeExpiry = (value) => {
  let v = value.replace(/\D/g, ''); 
  if (v.length >= 2) {
    let month = parseInt(v.slice(0, 2));
    if (month > 12) v = '12' + v.slice(2);
    else if (month === 0 && v.length === 2) v = '01';
  }
  if (v.length >= 3) return `${v.slice(0, 2)}/${v.slice(2, 6)}`;
  return v;
};

const GRN = () => {
  const [view, setView] = useState('form');
  const { user } = useContext(AuthContext);
  const [grns, setGrns] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [editId, setEditId] = useState(null);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [nextGrnId, setNextGrnId] = useState('');
  const [nextGrnNumber, setNextGrnNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => {
    return {
      ...emptyForm,
      received_by: user?.fullName || '' 
    };
  });
  const [poInputValue, setPoInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchGRNs();
    
    fetchPOs();
    fetchSuppliers();
    fetchMedicines();
  }, []);

  // Auto-fills received_by field once global user state arrives
  useEffect(() => {
    if (user?.fullName && !editId) {
      setFormData(prev => ({ ...prev, received_by: user.fullName }));
    }
  }, [user, editId]);
  const fetchGRNs = async () => {
    try { const res = await api.get('/grn'); setGrns(res.data || []); } catch (err) {}
  };

const fetchNextNumber = async () => {
    try {
      const res = await api.get('/grn/next-number');
      const nextId = res.data?.grnId || '';
      const nextNumber = res.data?.grnNumber || '';
      setNextGrnId(nextId);
      setNextGrnNumber(nextNumber);
      setFormData(prev => ({ 
        ...prev, 
        grn_id: nextId, 
        grn_number: nextNumber 
      }));
      return { nextId, nextNumber };
    } catch (err) {}
    return { nextId: '', nextNumber: '' };
  };

const openCreateForm = () => {
    setView('form');
    setEditId(null);
    setPoInputValue('');
    setFormData({
      ...emptyForm,
      received_date: toDateInputValue(new Date()),
      received_by: user?.fullName || ''
    });
  };

  const fetchPOs = async () => {
    try { const res = await api.get('/purchase-orders'); setPurchaseOrders(res.data || []); } catch (err) {}
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/master/suppliers/all');
      setSuppliers(res.data?.success ? res.data.data : res.data || []);
    } catch (err) {}
  };

  const fetchMedicines = async () => {
    try {
      const res = await api.get('/master/medicine/all');
      setMedicines(Array.isArray(res.data?.data) ? res.data.data : res.data || []);
    } catch (err) {}
  };

const medicineMap = useMemo(() => {
    const map = new Map();
    medicines.forEach((m) => {
      if (m?._id) map.set(String(m._id).trim(), m);
      if (m?.medicine_id) map.set(String(m.medicine_id).trim(), m);
    });
    return map;
  }, [medicines]);

  const toDateInput = (value) => toDateInputValue(value);
  const formatDate = (value) => formatDisplayDate(value);

  const selectedPo = useMemo(() => {
    if (!formData.po_id) return null;
    return purchaseOrders.find(p => p.po_id === formData.po_id) || null;
  }, [purchaseOrders, formData.po_id]);

  const getMedicineKey = useCallback((value) => {
    if (!value) return '';
    return String(value?.medicine_id || value?._id || value).trim();
  }, []);

  const getPoItemKey = useCallback((value) => {
    if (!value) return '';
    return String(value?.po_item_id || value?._id || value).trim();
  }, []);

const buildPendingItemsForPo = useCallback((matchedPo) => {
    if (!matchedPo || !Array.isArray(matchedPo.items)) return [];

    const poNoToMatch = normalizePoLookup(matchedPo.po_number || matchedPo.po_id);
    const totalReceivedByPoItem = {};
    const totalReceivedByMedicine = {};

    grns.forEach((grn) => {
        if (!Array.isArray(grn?.items)) return;

        grn.items.forEach((item) => {
            const itemPoNo = normalizePoLookup(item?.po_number || grn?.po_number || grn?.po_id);

            if (itemPoNo === poNoToMatch) {
                const qty = Number(item?.received_quantity) || 0;
                const poItemId = getPoItemKey(item);
                const medId = getMedicineKey(item?.medicine_id);

                if (poItemId) {
                  totalReceivedByPoItem[poItemId] = (totalReceivedByPoItem[poItemId] || 0) + qty;
                }

                if (medId) {
                  totalReceivedByMedicine[medId] = (totalReceivedByMedicine[medId] || 0) + qty;
                }
            }
        });
    });

    const processedPendingItems = matchedPo.items.map((item) => {
        const poItemId = getPoItemKey(item);
        const directId = getMedicineKey(item?.medicine_id);
        const masterMatch = medicineMap.get(directId);
        const targetDropdownValue = masterMatch?.medicine_id || directId;

        if (!targetDropdownValue) return null;

        const originalQty = Number(item?.ordered_quantity) || 0;
        const alreadyReceivedTotal = poItemId
          ? (totalReceivedByPoItem[poItemId] || 0)
          : (
              (totalReceivedByMedicine[directId] || 0) ||
              (totalReceivedByMedicine[targetDropdownValue] || 0)
            );

        const trueBalance = originalQty - alreadyReceivedTotal;

        if (trueBalance <= 0) return null;

        return {
            po_item_id: poItemId,
            medicine_id: targetDropdownValue,
            batch_number: '',
            mfg_date: '',
            expiry_date: '',
            ordered_quantity: trueBalance,
            received_quantity: '',
            purchase_price: Number(item?.purchase_price) || 0,
            pending_quantity: trueBalance,
            mrp: '',
            po_number: matchedPo.po_number || matchedPo.po_id,
            item_source: "PREVIOUS_PENDING",
            is_extra_fill: alreadyReceivedTotal > 0
        };
    });

    return processedPendingItems.filter(Boolean);
}, [getMedicineKey, getPoItemKey, grns, medicineMap]);

const applyPurchaseOrder = (value) => {
    const normalized = normalizePoLookup(value);
    if (!normalized) return;

    const matchedPo = purchaseOrders.find(po => 
      normalizePoLookup(po?.po_number) === normalized || 
      normalizePoLookup(po?.po_id) === normalized
    );
    
    if (!matchedPo) { 
        toast.error("PO Not Found"); 
        return; 
    }

    // --- NEW CHANGE START ---
    // 1. Check if the PO is already fully received (CLOSED)
    if (matchedPo.po_final_status === 'CLOSED') {
        toast.error(`PO ${matchedPo.po_number || matchedPo.po_id} is already CLOSED. All items received.`);
        return;
    }
    // --- NEW CHANGE END ---

    // 2. Check if the PO is Approved (Paper check)
    if (matchedPo.status !== 'Approved') { 
        toast.error(`PO is ${matchedPo.status}. Only Approved POs allowed.`); 
        return; 
    }
    
    const sId = matchedPo.supplier_id; 
    const pendingItems = buildPendingItemsForPo(matchedPo);

    if (pendingItems.length === 0) {
        toast.error(`PO ${matchedPo.po_number || matchedPo.po_id} has no pending medicines.`);
        return;
    }

    setPoInputValue(matchedPo.po_number || matchedPo.po_id);
    
    setFormData(prev => ({ 
        ...prev, 
        supplier_id: sId, 
        po_id: matchedPo.po_id, 
        po_number: matchedPo.po_number,
        items: pendingItems
    }));
};
  // Helper to ensure values always show two decimals like 34.00
const formatDecimal = (val) => {
  const num = Number(val) || 0;
  return num.toFixed(2);
};
const handleItemChange = (index, field, value) => {
  const newItems = [...formData.items];

  // 1. Handle specialized formatting for specific fields
  if (field === 'expiry_date') {
    newItems[index][field] = normalizeExpiry(value);
  } else if (field === 'conversion_factor') {
    // Ensure CF is stored as a number
    newItems[index][field] = value === "" ? "" : Number(value);
  } else {
    newItems[index][field] = value;
  }

  // 2. Logic when MEDICINE is selected/changed
  if (field === 'medicine_id') {
    if (value) {
      // RESET Conversion Factor so dropdown shows "--" placeholder until user picks one
      newItems[index].conversion_factor = '';

      // Auto-fill details from Purchase Order (Price, MRP, Ordered Qty)
      const currentRowPo = newItems[index].po_number || formData.po_id;
      const targetPo = purchaseOrders.find(p => p.po_id === currentRowPo || p.po_number === currentRowPo);
      
      if (targetPo) {
        const currentPoItemId = getPoItemKey(newItems[index]);
        const poItem = targetPo.items.find(i => {
          if (currentPoItemId) return getPoItemKey(i) === currentPoItemId;
          return getMedicineKey(i.medicine_id) === value;
        });

       if (poItem) {
          const totalOrdered = Number(poItem.ordered_quantity) || 0;
          const totalReceived = Number(poItem.received_quantity) || 0;
          const pendingQty = Math.max(totalOrdered - totalReceived, 0);

          newItems[index].po_item_id = getPoItemKey(poItem);
          newItems[index].ordered_quantity = currentPoItemId ? pendingQty : totalOrdered;
          newItems[index].purchase_price = poItem.purchase_price || 0;
          newItems[index].mrp = ''; 
        }
      }
    } else {
      // If medicine is cleared, clear relevant fields
      newItems[index].conversion_factor = '';
      newItems[index].ordered_quantity = '';
      newItems[index].purchase_price = '';
      newItems[index].mrp = '';
    }
  }

  // 3. Recalculate PENDING QUANTITY
  // Triggered if quantity, medicine, or ordered quantity changes
  if (field === 'received_quantity' || field === 'medicine_id' || field === 'ordered_quantity') {
    newItems.forEach((item) => {
      if (!item.medicine_id) return;
      const orderedQty = Number(item.ordered_quantity) || 0;
      const receivedQty = Number(item.received_quantity) || 0;
      item.pending_quantity = Math.max(orderedQty - receivedQty, 0);
    });
  }

  // 4. Update the global Form State
  setFormData({ ...formData, items: newItems });
};

const addOtherPurchaseOrder = (otherPoValue) => {
    const normalized = normalizePoLookup(otherPoValue);
    if (!normalized) return;

    const matchedPo = purchaseOrders.find(po => 
      normalizePoLookup(po?.po_id) === normalized || 
      normalizePoLookup(po?.po_number) === normalized
    );

    if (!matchedPo) { 
      toast.error("Additional PO Not Found"); 
      return; 
    }

    // --- ADDED BLOCKING LOGIC ---
    // Check if the PO is already fully received
    if (matchedPo.po_final_status === 'CLOSED') {
      toast.error(`PO ${matchedPo.po_number || matchedPo.po_id} is already CLOSED.`);
      return;
    }
    // ----------------------------

    const newPoItems = buildPendingItemsForPo(matchedPo);

    if (newPoItems.length === 0) {
      toast.error(`PO ${matchedPo.po_number || matchedPo.po_id} has no pending medicines.`);
      return;
    }

    setFormData(prev => ({ ...prev, items: [...prev.items, ...newPoItems] }));
    toast.success(`Items from ${matchedPo.po_number || matchedPo.po_id} added`);
};

  const addItemRow = () => {
    setFormData({ ...formData, items: [...formData.items, createEmptyItem()] });
  };

  const removeItemRow = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length ? newItems : [createEmptyItem()] });
  };

  const handleEdit = (grn) => {
    setEditId(grn._id);
    const actualPO = purchaseOrders.find(p => p.po_id === grn.po_id);
    setPoInputValue(actualPO ? actualPO.po_number : (grn.po_id || ''));

    setFormData({
      ...grn,
      invoice_date: toDateInput(grn.invoice_date),
      received_date: toDateInput(grn.received_date),
      items: (grn.items || []).map(item => ({ 
        ...item, 
        mfg_date: toDateInput(item.mfg_date), 
        expiry_date: item.expiry_date,
        po_item_id: item.po_item_id || '',
        conversion_factor: item.conversion_factor || '',
        pending_quantity: Math.max((Number(item.ordered_quantity) || 0) - (Number(item.received_quantity) || 0), 0)
      }))
    });
    setView('form');
  };

const handleDelete = async (id) => {
    if (!window.confirm('Delete this GRN? This will recalculate and reverse all stock inventory values.')) return;

    try {
      await api.delete(`/grn/${id}`);
      toast.success('GRN Deleted');
      fetchGRNs();
      
    } catch (err) { 
      toast.error('Delete failed'); 
    }
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Validate if supplier exists (means at least one PO was processed)
    if (!formData.supplier_id) {
        toast.error("Please enter a PO number and press Enter first");
        return;
    }

    try {
      setSubmitting(true);
        const norm = (v) => toDateObject(v) ? toDateObject(v).toISOString() : undefined;

        // Global Identifiers
        const globalGrnNumber = editId ? formData.grn_number : (formData.grn_number || nextGrnNumber);
        const currentGrnId = editId ? formData.grn_id : (formData.grn_id || nextGrnId);

        const globalPendingList = formData.items
          .filter(i => i.medicine_id)
          .map(i => {
            const qtyOrdered = Number(i.ordered_quantity) || 0;
            const qtyReceived = Number(i.received_quantity) || 0;
            const balance = qtyOrdered - qtyReceived;

            return {
              po_item_id: i.po_item_id || '',
              medicine_id: i.medicine_id,
              pending_balance: balance > 0 ? balance : 0,
              po_number: i.po_number || poInputValue
            };
          })
          .filter(i => i.pending_balance > 0);

        // 2. GENERATE PO STATUS UPDATES (Checks every PO in this GRN against full history)
        const poNumbersInForm = [...new Set(formData.items.map(i => i.po_number))].filter(Boolean);
        
        const poStatusUpdates = poNumbersInForm.map(poNo => {
            const originalPo = purchaseOrders.find(p => p.po_number === poNo);
            if (!originalPo) return null;

            // Total quantity originally ordered in the Master PO
            const totalOrderedInPO = originalPo.items.reduce((sum, i) => sum + (Number(i.ordered_quantity) || 0), 0);

            // Sum of what was received in PREVIOUS saved GRNs
            const qtyFromHistory = grns
                .filter(g => g._id !== editId) // Exclude current doc if editing
                .flatMap(g => g.items)
                .filter(item => item.po_number === poNo)
                .reduce((sum, i) => sum + (Number(i.received_quantity) || 0), 0);

            // Sum of what is being received in THIS form right now
            const qtyInCurrentForm = formData.items
                .filter(i => i.po_number === poNo)
                .reduce((sum, i) => sum + (Number(i.received_quantity) || 0), 0);

            const isPoComplete = (qtyFromHistory + qtyInCurrentForm) >= totalOrderedInPO;

            return {
                po_number: poNo,
                po_final_status: isPoComplete ? 'CLOSED' : 'OPEN',
                grn_id: currentGrnId // Mentioning current grn_id as requested
            };
        }).filter(Boolean);

        // 3. PREPARE THE PAYLOAD
        const payload = {
            ...formData,
            grn_number: globalGrnNumber,
            invoice_date: norm(formData.invoice_date),
            received_date: norm(formData.received_date),
            
            // This stores the fulfillment logic results in the GRN document
            po_status_updates: poStatusUpdates,

            items: formData.items
                .filter(i => i.medicine_id && (Number(i.received_quantity) > 0))
                .map(i => {
                    const qtyOrdered = Number(i.ordered_quantity) || 0;
                    const qtyReceived = Number(i.received_quantity) || 0;
                    const diff = qtyOrdered - qtyReceived;
                   const cf = i.conversion_factor || 1;

                    return {
                        po_item_id: i.po_item_id || '',
                        medicine_id: i.medicine_id,
                        batch_number: i.batch_number,
                        mfg_date: norm(i.mfg_date),
                        expiry_date: i.expiry_date,
                        ordered_quantity: qtyOrdered,
                        received_quantity: qtyReceived,
                        purchase_price: Number(i.purchase_price) || 0,
                        conversion_factor: Number(cf),
                        mrp: Number(i.mrp) || 0,
                        per_medicine_price: cf > 0 ? (Number(i.mrp) / Number(cf)) : Number(i.mrp),
                        po_number: i.po_number || poInputValue, 
                        grn_number: globalGrnNumber,
                        item_source: i.item_source || "MAIN_PO",
                        is_extra_fill: i.is_extra_fill || false,
                        extra_quantity: diff < 0 ? Math.abs(diff) : 0
                    };
                }),

            pending_medicines: globalPendingList
        };

        // 4. API CALL
        if (editId) {
            await api.put(`/grn/${editId}`, payload);
            toast.success(`GRN Updated Successfully`);
        } else {
            await api.post('/grn/create', payload);
            toast.success(`GRN Created and PO statuses checked`);
        }

       // 5. RESET & REFRESH
      setView('form');
      setEditId(null);
      setPoInputValue('');
      setFormData({
        ...emptyForm,
        received_date: toDateInputValue(new Date()),
        received_by: user?.fullName || ''
      });
      fetchGRNs();

    } catch (err) {
        console.error("Submission Error:", err);
        toast.error(err.response?.data?.message || 'Failed to save GRN.');
    }finally {
        setSubmitting(false);
    }
};

  const filteredGrns = useMemo(() => {
    const sortedList = [...grns].sort((a, b) => {
      const idA = String(a.grn_number || a.grn_id || a._id || '');
      const idB = String(b.grn_number || b.grn_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((g) => {
      const grnNum = String(g.grn_number || '').toLowerCase();
      const poId = String(g.po_id || '').toLowerCase();
      const supplierName = String(
        suppliers.find((s) => s.supplier_id === g.supplier_id || s._id === g.supplier_id)?.supplier_name || g.supplier_id || ''
      ).toLowerCase();

      return (
        grnNum.startsWith(q) ||
        poId.startsWith(q) ||
        supplierName.startsWith(q)
      );
    });
  }, [grns, searchTerm, suppliers]);

  const paginatedGrns = useMemo(() => {
    if (pageSize === -1) return filteredGrns;
    const start = (currentPage - 1) * pageSize;
    return filteredGrns.slice(start, start + pageSize);
  }, [filteredGrns, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredGrns.length, pageSize]);


const fetchPendingFromPrevious = (poNo) => {
    const normalizedSearch = normalizePoLookup(poNo);
    
    // 1. Find the original PO to get the "Ordered Quantity"
    const matchedPo = purchaseOrders.find(po => 
        normalizePoLookup(po?.po_id) === normalizedSearch || 
        normalizePoLookup(po?.po_number) === normalizedSearch
    );
    
    if (!matchedPo) return toast.error("PO Not Found in Master List");
    const pendingItems = buildPendingItemsForPo(matchedPo);

    if (pendingItems.length === 0) {
        return toast.error("This PO is already 100% completed.");
    }

    setFormData(prev => ({ 
        ...prev, 
        items: [...prev.items, ...pendingItems] 
    }));
    toast.success(`Fetched latest balance: ${pendingItems.length} items remaining`);
};

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><ClipboardList size={10} /></div>
          <div><h1 className={masterTitle}>GRN</h1></div>
        </div>
        <div className="flex gap-2">
          {view === 'list' && (
          <button
            onClick={openCreateForm}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add GRN
          </button>
        )}
          {view !== 'list' && <button onClick={() => setView('list')} className={masterPrimaryButton}><ArrowLeft size={14} /> VIEW LIST</button>}
        </div>
      </header>

      <main className="mx-auto max-w-7xl">
        {view === 'list' && (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
           <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by GRN number, PO number, or supplier..." 
                totalItems={filteredGrns.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
              <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr>
                  <th className={masterTableData}>S.No</th>
                  <th className={masterTableData}>GRN No</th>
                  <th className={masterTableData}>PO No</th>
                  <th className={masterTableData}>Supplier</th>
                  <th className={masterTableData}>Date</th>
                  <th className={masterTableData}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                {grns.slice((currentPage-1)*pageSize, currentPage*pageSize).map((g, i) => (
                  <tr key={g._id} className="hover:bg-emerald-50/30">
                    <td className={masterTableData}>{(currentPage-1)*pageSize + i + 1}</td>
                    <td className={masterTableID}>{g.grn_number}</td>
                    <td className={masterTableData}>{purchaseOrders.find(p => p.po_id === g.po_id)?.po_number || g.po_id}</td>
                    <td className={masterTableData}>{suppliers.find(s => (s.supplier_id === g.supplier_id || s._id === g.supplier_id))?.supplier_name || g.supplier_id}</td>
                    <td className={masterTableData}>{formatDate(g.received_date)}</td>
                    <td className={masterTableData}>
                      <button onClick={() => { setSelectedGRN(g); setView('details'); }} className="p-1 text-emerald-600 hover:bg-emerald-100 rounded-lg"><Eye size={14} /></button>
                      <button onClick={() => handleEdit(g)} className="ml-1 p-1 text-sky-600 hover:bg-sky-100 rounded-lg"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(g._id)} className="ml-1 p-1 text-rose-600 hover:bg-rose-100 rounded-lg"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

{view === 'form' && (
  <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-0.5 bg-white border border-slate-300 rounded-xl`}>
    {/* 1. TOP PO FIELDS GRID */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
      {/* <ReadOnly label="GRN No" value={editId ? formData.grn_number : nextGrnNumber} /> */}
      <div className="flex flex-col">
        <label className={masterInputLabel}>PO Number (Enter)</label>
        <input name="po_no" className={masterInput} value={poInputValue} onChange={(e) => setPoInputValue(e.target.value)} onKeyDown={(e) => {if(e.key === 'Enter'){e.preventDefault(); applyPurchaseOrder(poInputValue);}}} placeholder="PO NUMBER" />
      </div>
      <Input label="Supplier" value={suppliers.find(s => s.supplier_id === formData.supplier_id)?.supplier_name || ''} disabled />
      <Input label="Received Date" value={formData.received_date} onChange={(e) => setFormData({...formData, received_date: normalizeDateInput(e.target.value)})} />
      <Input label="Invoice No" value={formData.invoice_number} onChange={(e) => setFormData({...formData, invoice_number: e.target.value})} />
      <Input label="Invoice Date" value={formData.invoice_date} onChange={(e) => setFormData({...formData, invoice_date: normalizeDateInput(e.target.value)})} />
      
      <ReadOnly label="Received By" value={formData.received_by || user?.fullName || ''} />
      <Input label="Remarks" value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})} />
    </div>

    {/* 2. HR LINE THEN PENDING PO FIELD */}
    <hr className="border-slate-200" />
    <div className="bg-blue-50 p-0.5 rounded-lg border border-blue-200">
      <label className="text-[10px] font-bold text-blue-700 uppercase block">Previous Pending PO Number (Enter to fetch)</label>
      <input 
        type="text" 
        className={`${masterInput} h-5 w-full max-w-sm`} 
        placeholder="Type PO Number & Press Enter..." 
        onKeyDown={(e) => {
          if (e.key === 'Enter') { 
            e.preventDefault(); 
            fetchPendingFromPrevious(e.target.value); 
            e.target.value = ''; 
          }
        }}
      />
    </div>

<div className="border-t pt-1">
  <div className="flex justify-between">
    <h3 className="text-[11px] font-black uppercase text-emerald-600">GRN Items</h3>
    <button type="button" onClick={addItemRow} className="text-[10px] font-bold text-indigo-600 hover:underline">+ ADD ROW</button>
  </div>
   <div className="max-h-[350px] overflow-auto">
     <table className="w-full min-w-[900px] table-fixed text-left text-[10px]">
        <thead className="sticky top-0 border-b border-slate-300 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.12em] text-emerald-600">
        <tr>
          <th className="p-0.5 w-[3%] text-center">Sl</th>
          <th className="p-0.5 w-[20%] text-left">Medicine Name</th>
          <th className="p-0.5 w-[4%] text-center">CF</th>
          <th className="p-0.5 w-[9%] text-right">Price</th>
          <th className="p-0.5 w-[8%] text-right">Ord Qty</th>
          <th className="p-0.5 w-[12%] text-center">Batch</th>
          <th className="p-0.5 w-[12%] text-center">Expiry</th>
          <th className="p-0.5 w-[9%] text-right">Chalan</th>
          <th className="p-0.5 w-[8%] text-right">MRP</th>
          <th className="p-0.5 w-[3%] text-center">X</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200 bg-white">
        {formData.items.map((item, index) => (
          <tr key={index}>
            <td className="p-0.5 text-center font-bold text-slate-600">{index + 1}</td>
            
            {/* MEDICINE COLUMN - DISABLED UNTIL PO IS ENTERED */}
            <td className="p-0.5">
              <select 
                className={`${masterSelect} w-full h-5 py-0 text-[10px] ${!formData.supplier_id ? 'bg-slate-50 cursor-not-allowed' : ''}`} 
                value={item.medicine_id} 
                onChange={(e) => handleItemChange(index, 'medicine_id', e.target.value)}
                disabled={!formData.supplier_id} // Logic: Disabled if no PO/Supplier is found
              >
                <option value="">{formData.supplier_id ? 'SELECT MEDICINE' : 'ENTER PO FIRST'}</option>
                {medicines.map((m, i) => (
                  <option key={i} value={m.medicine_id}>{m.medicine_name}</option>
                ))}
              </select>
            </td>

            {/* CF COLUMN - SHOWS -- AND DISABLED UNTIL MEDICINE SELECTED */}
            <td className="p-0.5">
              <select 
                className={`${masterSelect} h-7 w-full text-center bg-blue-50 font-black text-blue-700 !px-0 text-[9px] cursor-pointer`}
                value={item.conversion_factor || ''}
                onChange={(e) => handleItemChange(index, 'conversion_factor', e.target.value)}
                disabled={!item.medicine_id}
              >
                {!item.medicine_id ? (
                  <option value="">--</option>
                ) : (
                  <>
                    <option value="">--</option>
                    {(() => {
                      const medData = medicineMap.get(item.medicine_id);
                      const factors = Array.isArray(medData?.conversion_factor) 
                        ? medData.conversion_factor 
                        : (medData?.conversion_factor ? [medData.conversion_factor] : []);
                        
                      return factors.map((cf, i) => (
                        <option key={i} value={cf}>{cf}</option>
                      ));
                    })()}
                  </>
                )}
              </select>
            </td>

            <td className="p-0.5">
              <input type="number" className={`${masterInput} text-right h-5`} value={item.purchase_price} onChange={(e) => handleItemChange(index, 'purchase_price', e.target.value)} />
              </td>
            <td className="p-0.5">
              <input type="number" className={`${masterInput} text-right h-5`} value={item.ordered_quantity} onChange={(e) => handleItemChange(index, 'ordered_quantity', e.target.value)} />
              </td>
            <td className="p-0.5">
              <input className={`${masterInput} text-center h-5`} value={item.batch_number} onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)} />
              </td>
            <td className="p-0.5">
              <input className={`${masterInput} text-center h-5`} value={item.expiry_date} placeholder="MM/YYYY" onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)} />
              </td>
            <td className="p-0.5">
              <input type="number" className={`${masterInput} text-right h-5`} value={item.received_quantity} onChange={(e) => handleItemChange(index, 'received_quantity', e.target.value)} />
              </td>
            <td className="p-0.5">
              <input type="number" className={`${masterInput} text-right h-5`} value={item.mrp} onChange={(e) => handleItemChange(index, 'mrp', e.target.value)} />
              </td>
            <td className="p-0.5 text-center">
              <button type="button" onClick={() => removeItemRow(index)} className="text-rose-600">
                <Trash2 size={12} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

    {/* 4. NEW HR LINE THEN PENDING MEDICINE SUMMARY */}
    {/* <hr className="border-slate-300 my-4" />
    <div className="bg-orange-50/50 p-3 rounded-xl border border-orange-200">
      <h3 className="text-[12px] font-black uppercase text-orange-700 mb-3 underline">Pending Stock Details</h3>
      <div className="overflow-hidden rounded-lg border border-orange-300 bg-white shadow-sm">
        <table className="w-full text-[10px]">
          <thead className="bg-orange-100 text-orange-800 font-bold uppercase border-b">
            <tr>
              <th className="p-2 text-left">Medicine</th>
              <th className="p-2 text-right">Pending Qty</th>
              <th className="p-2 text-right">Price</th>
              <th className="p-2 text-center">Reference PO</th>
            </tr>
          </thead>
          <tbody>
            {formData.items.filter(i => (Number(i.pending_quantity) > 0)).map((pItem, pIdx) => (
              <tr key={pIdx} className="border-b border-orange-100 last:border-0 font-bold">
                <td className="p-2 text-slate-700">{medicineMap.get(pItem.medicine_id)?.medicine_name || pItem.medicine_id}</td>
                <td className="p-2 text-right text-orange-600 font-black">{pItem.pending_quantity}</td>
                <td className="p-2 text-right text-slate-500">{pItem.purchase_price}</td>
                <td className="p-2 text-center font-mono text-slate-400 text-[9px]">{pItem.po_number || poInputValue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div> */}

    <div className="flex justify-end pt-1">
      <button type="submit" className={`${masterAccentButton} px-5`}>SAVE GRN</button>
    </div>
  </form>
)}

        {view === 'details' && selectedGRN && (
          <div className={`${masterPanelPadding} bg-white rounded-xl border border-slate-300 shadow-sm animate-in fade-in duration-300`}>
            <div className="flex justify-between items-start mb-6 border-b pb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedGRN.grn_number}</h2>
                <p className="text-xs text-slate-500 uppercase font-bold">GRN Details</p>
                </div>
              <button onClick={() => setView('list')} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 text-xs">
              <div>
                <label className="block text-slate-400 font-bold uppercase mb-1">Supplier</label>
                <p className="font-bold text-slate-700">{suppliers.find(s => (s.supplier_id === selectedGRN.supplier_id || s._id === selectedGRN.supplier_id))?.supplier_name || selectedGRN.supplier_id}</p></div>
              <div><label className="block text-slate-400 font-bold uppercase mb-1">PO Number</label>
              <p className="font-bold text-slate-700">{purchaseOrders.find(p => p.po_id === selectedGRN.po_id)?.po_number || selectedGRN.po_id}</p>
              </div>
              <div><label className="block text-slate-400 font-bold uppercase mb-1">Received Date</label>
              <p className="font-bold text-slate-700">{formatDate(selectedGRN.received_date)}</p>
              </div>
              {/* <div><label className="block text-slate-400 font-bold uppercase mb-1">Invoice Info</label><p className="font-bold text-slate-700">{selectedGRN.invoice_number || '-'} ({formatDate(selectedGRN.invoice_date)})</p></div> */}
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-slate-50 uppercase text-slate-500 font-black border-b">
                  <tr><th className="p-3">Medicine</th>
                  <th className="p-3 text-right">Price</th>
                  <th className="p-3 text-right">Ord Qty</th>
                  <th className="p-3">Batch</th>
                  <th className="p-3 text-center">Expiry</th>
                  <th className="p-3 text-right">Chalan Qty</th>
                  {/* <th className="p-3 text-right">Pending/Extra</th> */}
                  <th className="p-3 text-right">MRP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedGRN.items.map((item, idx) => {
                    const diff = (Number(item.ordered_quantity) || 0) - (Number(item.received_quantity) || 0);
                    return (
                        <tr key={idx} className="font-bold text-slate-600">
                            <td className="p-3">{medicineMap.get(item.medicine_id)?.medicine_name.toUpperCase() || item.medicine_id}</td>
                            <td className="p-3 text-right">{item.purchase_price}</td>
                            <td className="p-3 text-right">{item.ordered_quantity}</td>
                            <td className="p-3">{item.batch_number.toUpperCase()}</td>
                            <td className="p-3 text-center">{item.expiry_date}</td>
                            <td className="p-3 text-right text-emerald-600">{item.received_quantity}</td>
                            {/* <td className={`p-3 text-right ${diff < 0 ? 'text-rose-600' : ''}`}>
                                {diff < 0 ? `EXTRA: ${Math.abs(diff)}` : diff}
                            </td> */}
                            <td className="p-3 text-right">{item.mrp}</td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input {...props} className={masterInput} />
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <div className={masterReadOnly}>{value}</div>
  </div>
);

export default GRN;
